import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, FiRefreshCw, FiFileText, FiExternalLink, FiEdit2, FiTrash2, 
  FiCheck, FiClock, FiAlertTriangle, FiUpload, FiDownload, FiFilter,
  FiX, FiSave, FiCalendar, FiZap, FiLoader, FiEye, FiMaximize2
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import FilePreviewModal from '@/components/dataroom/FilePreviewModal';

type InvoiceProject = 'cab_bot_v2' | 'snap_chart_ai';
type InvoiceStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

interface CompanyInvoice {
  id: string;
  project: InvoiceProject;
  vendor_name: string;
  description: string | null;
  amount_usd: number;
  invoice_date: string;
  due_date: string | null;
  paid_date: string | null;
  status: InvoiceStatus;
  tx_hash: string | null;
  receipt_url: string | null;
  receipt_file_path: string | null;
  notes: string | null;
  created_at: string;
}

interface ParsedInvoice {
  vendor_name: string;
  description: string;
  amount_usd: number;
  invoice_date: string;
  due_date: string | null;
  project: InvoiceProject;
  notes: string | null;
  file?: File;
}

type FilterPeriod = 'all' | 'week' | 'month' | 'year';

const PROJECT_LABELS: Record<InvoiceProject, string> = {
  cab_bot_v2: 'Cab Bot v2',
  snap_chart_ai: 'Snap Chart AI'
};

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', color: 'yellow', icon: <FiClock className="w-3 h-3" /> },
  paid: { label: 'Paid', color: 'green', icon: <FiCheck className="w-3 h-3" /> },
  overdue: { label: 'Overdue', color: 'red', icon: <FiAlertTriangle className="w-3 h-3" /> },
  cancelled: { label: 'Cancelled', color: 'gray', icon: <FiX className="w-3 h-3" /> }
};

const CompanyInvoicesPanel: React.FC = () => {
  const [invoices, setInvoices] = useState<CompanyInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<InvoiceProject | 'all'>('all');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('year');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<CompanyInvoice | null>(null);
  const [uploading, setUploading] = useState(false);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  
  // Smart upload states
  const [showSmartUpload, setShowSmartUpload] = useState(false);
  const [parsingFiles, setParsingFiles] = useState(false);
  const [parsedInvoices, setParsedInvoices] = useState<ParsedInvoice[]>([]);
  const [parseProgress, setParseProgress] = useState({ current: 0, total: 0 });
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  
  // Preview modal states
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    fileUrl: string;
    fileName: string;
    mimeType: string;
  }>({ isOpen: false, fileUrl: '', fileName: '', mimeType: '' });
  
  // Receipt thumbnails cache
  const [receiptThumbnails, setReceiptThumbnails] = useState<Record<string, string>>({});

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('company_invoices')
        .select('*')
        .order('invoice_date', { ascending: false });

      if (selectedProject !== 'all') {
        query = query.eq('project', selectedProject);
      }

      // Apply date filter
      const now = new Date();
      if (filterPeriod === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        query = query.gte('invoice_date', weekAgo.toISOString().split('T')[0]);
      } else if (filterPeriod === 'month') {
        const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
        query = query.gte('invoice_date', monthAgo.toISOString().split('T')[0]);
      } else if (filterPeriod === 'year') {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        query = query.gte('invoice_date', yearStart.toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      if (error) throw error;
      setInvoices((data as CompanyInvoice[]) || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, filterPeriod]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  // Parse a single invoice using AI
  const parseInvoiceWithAI = async (file: File): Promise<ParsedInvoice | null> => {
    try {
      const base64 = await fileToBase64(file);
      
      const { data, error } = await supabase.functions.invoke('parse-invoice', {
        body: {
          imageBase64: base64,
          mimeType: file.type,
          fileName: file.name
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to parse');

      return {
        ...data.invoice,
        file
      };
    } catch (err) {
      console.error('Error parsing invoice:', file.name, err);
      return null;
    }
  };

  // Handle bulk smart upload
  const handleSmartUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter(f => 
      f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    
    if (fileArray.length === 0) return;

    setShowSmartUpload(true);
    setParsingFiles(true);
    setParsedInvoices([]);
    setParseErrors([]);
    setParseProgress({ current: 0, total: fileArray.length });

    const results: ParsedInvoice[] = [];
    const errors: string[] = [];

    for (let i = 0; i < fileArray.length; i++) {
      setParseProgress({ current: i + 1, total: fileArray.length });
      
      const parsed = await parseInvoiceWithAI(fileArray[i]);
      if (parsed) {
        results.push(parsed);
      } else {
        errors.push(`Failed to parse: ${fileArray[i].name}`);
      }
    }

    setParsedInvoices(results);
    setParseErrors(errors);
    setParsingFiles(false);
  };

  // Save all parsed invoices - AUTOMATICALLY marks as PAID
  const saveAllParsedInvoices = async () => {
    setUploading(true);
    try {
      for (const parsed of parsedInvoices) {
        // Insert invoice with status = 'paid' since user reviewed receipt
        const { data: insertedInvoice, error: insertError } = await supabase
          .from('company_invoices')
          .insert([{
            project: parsed.project,
            vendor_name: parsed.vendor_name,
            description: parsed.description,
            amount_usd: parsed.amount_usd,
            invoice_date: parsed.invoice_date,
            due_date: parsed.due_date,
            status: 'paid' as InvoiceStatus,  // Auto-mark as paid
            paid_date: new Date().toISOString().split('T')[0],  // Set paid date
            notes: parsed.notes
          }])
          .select()
          .single();

        if (insertError) throw insertError;

        // Upload the file as receipt
        if (parsed.file && insertedInvoice) {
          const fileExt = parsed.file.name.split('.').pop();
          const fileName = `${insertedInvoice.id}-${Date.now()}.${fileExt}`;
          const filePath = `receipts/${fileName}`;

          await supabase.storage
            .from('invoice-receipts')
            .upload(filePath, parsed.file);

          await supabase
            .from('company_invoices')
            .update({ receipt_file_path: filePath })
            .eq('id', insertedInvoice.id);
        }
      }

      fetchInvoices();
      setShowSmartUpload(false);
      setParsedInvoices([]);
    } catch (err) {
      console.error('Error saving invoices:', err);
    } finally {
      setUploading(false);
    }
  };

  // Load receipt thumbnail
  const loadReceiptThumbnail = async (invoiceId: string, filePath: string) => {
    if (receiptThumbnails[invoiceId]) return; // Already loaded
    
    try {
      const { data } = await supabase.storage
        .from('invoice-receipts')
        .createSignedUrl(filePath, 3600); // 1 hour cache
      
      if (data?.signedUrl) {
        setReceiptThumbnails(prev => ({ ...prev, [invoiceId]: data.signedUrl }));
      }
    } catch (err) {
      console.error('Error loading thumbnail:', err);
    }
  };

  // Open preview modal for receipt
  const openReceiptPreview = async (invoice: CompanyInvoice) => {
    if (!invoice.receipt_file_path) return;
    
    try {
      const { data } = await supabase.storage
        .from('invoice-receipts')
        .createSignedUrl(invoice.receipt_file_path, 300);
      
      if (data?.signedUrl) {
        const ext = invoice.receipt_file_path.split('.').pop()?.toLowerCase();
        const mimeType = ext === 'pdf' ? 'application/pdf' : `image/${ext}`;
        
        setPreviewModal({
          isOpen: true,
          fileUrl: data.signedUrl,
          fileName: `${invoice.vendor_name} - Receipt`,
          mimeType
        });
      }
    } catch (err) {
      console.error('Error opening preview:', err);
    }
  };

  // Load thumbnails for visible invoices
  useEffect(() => {
    invoices.forEach(inv => {
      if (inv.receipt_file_path) {
        loadReceiptThumbnail(inv.id, inv.receipt_file_path);
      }
    });
  }, [invoices]);

  // Update a parsed invoice before saving
  const updateParsedInvoice = (index: number, updates: Partial<ParsedInvoice>) => {
    setParsedInvoices(prev => prev.map((inv, i) => i === index ? { ...inv, ...updates } : inv));
  };

  // Remove a parsed invoice
  const removeParsedInvoice = (index: number) => {
    setParsedInvoices(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (file: File, invoiceId: string) => {
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${invoiceId}-${Date.now()}.${fileExt}`;
      const filePath = `receipts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('invoice-receipts')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Update invoice with file path
      await supabase
        .from('company_invoices')
        .update({ receipt_file_path: filePath })
        .eq('id', invoiceId);

      fetchInvoices();
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    if (!confirm('Delete this invoice?')) return;
    try {
      await supabase.from('company_invoices').delete().eq('id', id);
      fetchInvoices();
    } catch (err) {
      console.error('Error deleting invoice:', err);
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    try {
      await supabase
        .from('company_invoices')
        .update({ status: 'paid', paid_date: new Date().toISOString().split('T')[0] })
        .eq('id', id);
      fetchInvoices();
    } catch (err) {
      console.error('Error updating invoice:', err);
    }
  };

  // Calculate totals
  const totals = invoices.reduce((acc, inv) => {
    acc.total += Number(inv.amount_usd);
    if (inv.status === 'paid') acc.paid += Number(inv.amount_usd);
    if (inv.status === 'pending' || inv.status === 'overdue') acc.pending += Number(inv.amount_usd);
    return acc;
  }, { total: 0, paid: 0, pending: 0 });

  const getStatusBadge = (status: InvoiceStatus) => {
    const config = STATUS_CONFIG[status];
    const colorClasses: Record<string, string> = {
      yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      green: 'bg-green-500/20 text-green-400 border-green-500/30',
      red: 'bg-red-500/20 text-red-400 border-red-500/30',
      gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${colorClasses[config.color]}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20">
            <FiFileText className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Company Invoices</h3>
            <p className="text-sm text-muted-foreground">Vendor invoice management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm cursor-pointer hover:opacity-90 transition-opacity">
            <FiZap className="w-4 h-4" />
            Smart Upload
            <input
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg"
              multiple
              onChange={(e) => {
                if (e.target.files?.length) {
                  handleSmartUpload(e.target.files);
                }
              }}
            />
          </label>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm hover:opacity-90 transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            New Invoice
          </button>
          <button
            onClick={fetchInvoices}
            disabled={loading}
            className="p-2 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Smart Upload Drop Zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver('global');
        }}
        onDragLeave={() => setDragOver(null)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(null);
          if (e.dataTransfer.files?.length) {
            handleSmartUpload(e.dataTransfer.files);
          }
        }}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
          dragOver === 'global'
            ? 'border-amber-500 bg-amber-500/10'
            : 'border-border hover:border-muted-foreground/50'
        }`}
      >
        <FiZap className={`w-8 h-8 mx-auto mb-2 ${dragOver === 'global' ? 'text-amber-400' : 'text-muted-foreground'}`} />
        <p className="text-sm font-medium text-foreground">
          Drop invoices here for AI-powered auto-detection
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Supports PDF, PNG, JPG • Upload multiple files at once
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Project Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Project:</span>
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['all', 'cab_bot_v2', 'snap_chart_ai'] as const).map((project) => (
              <button
                key={project}
                onClick={() => setSelectedProject(project)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  selectedProject === project
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-muted/50'
                }`}
              >
                {project === 'all' ? 'All' : PROJECT_LABELS[project]}
              </button>
            ))}
          </div>
        </div>

        {/* Period Filter */}
        <div className="flex items-center gap-2">
          <FiFilter className="w-4 h-4 text-muted-foreground" />
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['week', 'month', 'year', 'all'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setFilterPeriod(period)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  filterPeriod === period
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-muted/50'
                }`}
              >
                {period === 'week' ? 'Week' : period === 'month' ? 'Month' : period === 'year' ? 'Year' : 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border border-border bg-card/50">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold text-foreground">${totals.total.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/10">
          <p className="text-sm text-green-400">Paid</p>
          <p className="text-2xl font-bold text-green-400">${totals.paid.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10">
          <p className="text-sm text-yellow-400">To Pay</p>
          <p className="text-2xl font-bold text-yellow-400">${totals.pending.toLocaleString()}</p>
        </div>
      </div>

      {/* Invoice List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No invoices found</div>
        ) : (
          invoices.map((invoice) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-xl border transition-all ${
                expandedInvoiceId === invoice.id 
                  ? 'border-purple-500/50 bg-card/80' 
                  : 'border-border bg-card/50 hover:bg-card/80'
              }`}
            >
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpandedInvoiceId(expandedInvoiceId === invoice.id ? null : invoice.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        invoice.project === 'cab_bot_v2' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {PROJECT_LABELS[invoice.project]}
                      </span>
                      {getStatusBadge(invoice.status)}
                      {invoice.receipt_file_path && (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                          <FiCheck className="w-3 h-3" /> Receipt
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-foreground">{invoice.vendor_name}</h4>
                    {invoice.description && (
                      <p className="text-sm text-muted-foreground">{invoice.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-3 h-3" />
                        {new Date(invoice.invoice_date).toLocaleDateString('en-US')}
                      </span>
                      {invoice.due_date && (
                        <span>Due: {new Date(invoice.due_date).toLocaleDateString('en-US')}</span>
                      )}
                    </div>
                    {invoice.notes && (
                      <p className="mt-2 text-sm italic text-muted-foreground border-l-2 border-purple-500/50 pl-2">
                        {invoice.notes}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-foreground">${Number(invoice.amount_usd).toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                      {invoice.receipt_url && (
                        <a
                          href={invoice.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
                          title="View receipt"
                        >
                          <FiExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {invoice.status === 'pending' && (
                        <button
                          onClick={() => handleMarkAsPaid(invoice.id)}
                          className="p-1.5 rounded-lg hover:bg-green-500/20 text-green-400 transition-colors"
                          title="Mark as paid"
                        >
                          <FiCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setEditingInvoice(invoice)}
                        className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Drag & Drop Upload Zone */}
              <AnimatePresence>
                {expandedInvoiceId === invoice.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4">
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(invoice.id);
                        }}
                        onDragLeave={() => setDragOver(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(null);
                          const file = e.dataTransfer.files?.[0];
                          if (file) {
                            handleFileUpload(file, invoice.id);
                          }
                        }}
                        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                          dragOver === invoice.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-border hover:border-muted-foreground/50'
                        }`}
                      >
                        {uploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <FiRefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
                            <p className="text-sm text-muted-foreground">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <FiUpload className={`w-8 h-8 ${dragOver === invoice.id ? 'text-purple-400' : 'text-muted-foreground'}`} />
                            <p className="text-sm text-foreground font-medium">
                              Drag & drop your receipt here
                            </p>
                            <p className="text-xs text-muted-foreground">
                              or click to browse from your computer
                            </p>
                            <label className="mt-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm cursor-pointer hover:opacity-90 transition-opacity">
                              Browse Files
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleFileUpload(e.target.files[0], invoice.id);
                                  }
                                }}
                              />
                            </label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Supports: PDF, PNG, JPG
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Show existing receipt with thumbnail */}
                      {invoice.receipt_file_path && (
                        <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                          <div className="flex items-start gap-3">
                            {/* Mini thumbnail preview */}
                            {receiptThumbnails[invoice.id] && (
                              <div 
                                className="relative w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-green-500/30 cursor-pointer hover:border-green-400 transition-colors group"
                                onClick={() => openReceiptPreview(invoice)}
                              >
                                {invoice.receipt_file_path.endsWith('.pdf') ? (
                                  <div className="w-full h-full bg-red-500/20 flex items-center justify-center">
                                    <FiFileText className="w-6 h-6 text-red-400" />
                                  </div>
                                ) : (
                                  <img 
                                    src={receiptThumbnails[invoice.id]} 
                                    alt="Receipt thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <FiMaximize2 className="w-4 h-4 text-white" />
                                </div>
                              </div>
                            )}
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <FiCheck className="w-4 h-4 text-green-400" />
                                <span className="text-sm font-medium text-green-400">Receipt uploaded</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openReceiptPreview(invoice)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:opacity-90 transition-opacity flex items-center gap-1"
                                >
                                  <FiEye className="w-3 h-3" />
                                  View Full
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const { data, error } = await supabase.storage
                                        .from('invoice-receipts')
                                        .createSignedUrl(invoice.receipt_file_path!, 300);
                                      
                                      if (error) throw error;
                                      if (!data?.signedUrl) throw new Error('No URL');
                                      
                                      // Get file extension from path
                                      const ext = invoice.receipt_file_path!.split('.').pop() || 'pdf';
                                      const fileName = `receipt-${invoice.vendor_name.replace(/[^a-zA-Z0-9]/g, '_')}.${ext}`;
                                      
                                      // Fetch the file and trigger download
                                      const response = await fetch(data.signedUrl);
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = fileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                    } catch (err) {
                                      console.error('Download error:', err);
                                      alert('Failed to download receipt');
                                    }
                                  }}
                                  className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                                >
                                  <FiDownload className="w-3 h-3" />
                                  Download
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || editingInvoice) && (
          <InvoiceModal
            invoice={editingInvoice}
            onClose={() => {
              setShowAddModal(false);
              setEditingInvoice(null);
            }}
            onSave={() => {
              fetchInvoices();
              setShowAddModal(false);
              setEditingInvoice(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Smart Upload Preview Modal */}
      <AnimatePresence>
        {showSmartUpload && (
          <SmartUploadModal
            parsing={parsingFiles}
            progress={parseProgress}
            parsedInvoices={parsedInvoices}
            errors={parseErrors}
            uploading={uploading}
            onUpdateInvoice={updateParsedInvoice}
            onRemoveInvoice={removeParsedInvoice}
            onSaveAll={saveAllParsedInvoices}
            onClose={() => {
              setShowSmartUpload(false);
              setParsedInvoices([]);
              setParseErrors([]);
            }}
          />
        )}
      </AnimatePresence>

      {/* Receipt Preview Modal */}
      <FilePreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, fileUrl: '', fileName: '', mimeType: '' })}
        fileUrl={previewModal.fileUrl}
        fileName={previewModal.fileName}
        mimeType={previewModal.mimeType}
        onDownload={async () => {
          try {
            // Fetch the file and trigger download via blob
            const response = await fetch(previewModal.fileUrl);
            if (!response.ok) throw new Error('Failed to fetch file');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = previewModal.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (err) {
            console.error('Download error:', err);
            // Fallback: try direct download
            const link = document.createElement('a');
            link.href = previewModal.fileUrl;
            link.download = previewModal.fileName;
            link.target = '_blank';
            link.click();
          }
        }}
      />
    </div>
  );
};

// Smart Upload Preview Modal
interface SmartUploadModalProps {
  parsing: boolean;
  progress: { current: number; total: number };
  parsedInvoices: ParsedInvoice[];
  errors: string[];
  uploading: boolean;
  onUpdateInvoice: (index: number, updates: Partial<ParsedInvoice>) => void;
  onRemoveInvoice: (index: number) => void;
  onSaveAll: () => void;
  onClose: () => void;
}

const SmartUploadModal: React.FC<SmartUploadModalProps> = ({
  parsing,
  progress,
  parsedInvoices,
  errors,
  uploading,
  onUpdateInvoice,
  onRemoveInvoice,
  onSaveAll,
  onClose
}) => {
  const totalAmount = parsedInvoices.reduce((sum, inv) => sum + inv.amount_usd, 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card rounded-2xl border border-border p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-600/20 to-orange-600/20">
              <FiZap className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Smart Invoice Upload</h3>
              <p className="text-sm text-muted-foreground">AI-detected invoice details</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted/50">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Parsing Progress */}
        {parsing && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-3">
              <FiLoader className="w-5 h-5 text-amber-400 animate-spin" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-400">
                  Analyzing invoices... ({progress.current}/{progress.total})
                </p>
                <div className="mt-2 h-2 bg-amber-500/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            {errors.map((error, i) => (
              <p key={i} className="text-sm text-red-400">{error}</p>
            ))}
          </div>
        )}

        {/* Parsed Invoices */}
        {!parsing && parsedInvoices.length > 0 && (
          <>
            <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-between">
              <span className="text-sm text-green-400">
                {parsedInvoices.length} invoice(s) detected
              </span>
              <span className="text-sm font-bold text-green-400">
                Total: ${totalAmount.toLocaleString()}
              </span>
            </div>

            <div className="space-y-4 mb-6">
              {parsedInvoices.map((invoice, index) => (
                <div key={index} className="p-4 rounded-xl border border-border bg-card/50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        invoice.project === 'cab_bot_v2' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {PROJECT_LABELS[invoice.project]}
                      </span>
                      <span className="text-xs text-muted-foreground">{invoice.file?.name}</span>
                    </div>
                    <button
                      onClick={() => onRemoveInvoice(index)}
                      className="p-1 rounded hover:bg-red-500/20 text-red-400"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Vendor</label>
                      <input
                        type="text"
                        value={invoice.vendor_name}
                        onChange={(e) => onUpdateInvoice(index, { vendor_name: e.target.value })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Amount (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={invoice.amount_usd}
                        onChange={(e) => onUpdateInvoice(index, { amount_usd: parseFloat(e.target.value) || 0 })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Description</label>
                      <input
                        type="text"
                        value={invoice.description}
                        onChange={(e) => onUpdateInvoice(index, { description: e.target.value })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Invoice Date</label>
                      <input
                        type="date"
                        value={invoice.invoice_date}
                        onChange={(e) => onUpdateInvoice(index, { invoice_date: e.target.value })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white [color-scheme:dark]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Project</label>
                      <select
                        value={invoice.project}
                        onChange={(e) => onUpdateInvoice(index, { project: e.target.value as InvoiceProject })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white"
                      >
                        <option value="snap_chart_ai">Snap Chart AI</option>
                        <option value="cab_bot_v2">Cab Bot v2</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Due Date</label>
                      <input
                        type="date"
                        value={invoice.due_date || ''}
                        onChange={(e) => onUpdateInvoice(index, { due_date: e.target.value || null })}
                        className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-800 text-white [color-scheme:dark]"
                      />
                    </div>
                  </div>

                  {invoice.notes && (
                    <div className="mt-2">
                      <label className="text-xs text-muted-foreground">Notes</label>
                      <p className="text-xs text-muted-foreground italic mt-1">{invoice.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSaveAll}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {uploading ? (
                  <FiLoader className="w-4 h-4 animate-spin" />
                ) : (
                  <FiCheck className="w-4 h-4" />
                )}
                Save All ({parsedInvoices.length})
              </button>
            </div>
          </>
        )}

        {/* No invoices parsed */}
        {!parsing && parsedInvoices.length === 0 && errors.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No invoices to display
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

interface InvoiceModalProps {
  invoice: CompanyInvoice | null;
  onClose: () => void;
  onSave: () => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ invoice, onClose, onSave }) => {
  const [form, setForm] = useState({
    project: invoice?.project || 'snap_chart_ai' as InvoiceProject,
    vendor_name: invoice?.vendor_name || '',
    description: invoice?.description || '',
    amount_usd: invoice?.amount_usd?.toString() || '',
    invoice_date: invoice?.invoice_date || new Date().toISOString().split('T')[0],
    due_date: invoice?.due_date || '',
    status: invoice?.status || 'pending' as InvoiceStatus,
    tx_hash: invoice?.tx_hash || '',
    receipt_url: invoice?.receipt_url || '',
    notes: invoice?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        amount_usd: parseFloat(form.amount_usd),
        due_date: form.due_date || null,
        tx_hash: form.tx_hash || null,
        receipt_url: form.receipt_url || null,
        notes: form.notes || null,
        description: form.description || null
      };

      if (invoice) {
        await supabase.from('company_invoices').update(data).eq('id', invoice.id);
      } else {
        await supabase.from('company_invoices').insert([data]);
      }
      onSave();
    } catch (err) {
      console.error('Error saving invoice:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card rounded-2xl border border-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            {invoice ? 'Edit Invoice' : 'New Invoice'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted/50">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Project</label>
            <select
              value={form.project}
              onChange={(e) => setForm({ ...form, project: e.target.value as InvoiceProject })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white"
            >
              <option value="cab_bot_v2">Cab Bot v2</option>
              <option value="snap_chart_ai">Snap Chart AI</option>
            </select>
          </div>

          {/* Vendor Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Vendor</label>
            <input
              type="text"
              value={form.vendor_name}
              onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white placeholder:text-slate-400"
              placeholder="Ex: Terraswitch Servers"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white placeholder:text-slate-400"
              placeholder="Ex: Usage Fees Credits"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Amount (USD)</label>
            <input
              type="number"
              step="0.01"
              value={form.amount_usd}
              onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white placeholder:text-slate-400"
              placeholder="1000.00"
              required
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Invoice Date</label>
              <input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white [color-scheme:dark]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as InvoiceStatus })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Transaction Hash */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Transaction Hash</label>
            <input
              type="text"
              value={form.tx_hash}
              onChange={(e) => setForm({ ...form, tx_hash: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white font-mono text-sm placeholder:text-slate-400"
              placeholder="0x..."
            />
          </div>

          {/* Receipt URL */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Receipt URL</label>
            <input
              type="url"
              value={form.receipt_url}
              onChange={(e) => setForm({ ...form, receipt_url: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white placeholder:text-slate-400"
              placeholder="https://solscan.io/tx/..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-slate-800 text-white resize-none placeholder:text-slate-400"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CompanyInvoicesPanel;
